import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/mergeMap';
import _ from 'lodash';

import { SmartenitPlugin } from '../smartenit-plugin';
import { TemperatureConverterService } from '../../common/temperature-converter.service';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';
import { ThermostatModes } from './thermostat-modes.enum';
import { INumericValue } from '../../interfaces/numeric-value.interface';
import { ITextValue } from '../../interfaces/text-value.interface';
import { IListValue } from '../../interfaces/list-value.interface';
import {
  NestService,
  nestDeviceTypes
} from '../../resources/external/nest.service';

const INIT_COOLING: number = 10;
const INIT_HEATING: number = 20;
const INIT_MODE: number = ThermostatModes.AUTO;
const CACHE_TIME: number = 3600;

export class ThermostatServerPlugin extends SmartenitPlugin
  implements INumericValue, ITextValue, IListValue {
  private _mode: any;
  private _temperature: number;
  private _desiredCoolingTemperature: number;
  private _desiredHeatingTemperature: number;

  public getReportableAttrs() {
    return ['LocalTemperature', 'PICoolingDemand', 'PIHeatingDemand'];
  }

  public setMode(mode: ThermostatModes, subscribe?: boolean): any {
    this._mode = mode;

    return this.setAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'SystemMode',
      { value: mode },
      subscribe || false
    );
  }

  public getValue(attribute?: string): number {
    let value: any = null;
    switch (attribute) {
      case 'OccupiedCoolingSetpoint':
        value = this._desiredCoolingTemperature;
        break;
      case 'OccupiedHeatingSetpoint':
        value = this._desiredHeatingTemperature;
        break;
      case 'SystemMode':
        value = this._mode;
        break;
    }

    return value;
  }

  public getUnit(attribute?: string): string {
    let value: string = '';

    switch (attribute) {
      case 'OccupiedCoolingSetpoint':
        value = this.temperatureConverterService.getUnit();
        break;
      case 'OccupiedHeatingSetpoint':
        value = this.temperatureConverterService.getUnit();
        break;
      case 'LocalTemperature':
        value = this.temperatureConverterService.getUnit();
        break;
      case 'SystemMode':
        value = '';
        break;
    }

    return value;
  }

  public getTextValue(): string {
    const temperatureUnit = this.temperatureConverterService.getUnit();
    let temperature = Math.floor(this._temperature);
    temperature = this._temperature
      ? Math.floor(
          this.temperatureConverterService.convert(
            temperature,
            TemperatureConverterService.DEFAULT_TEMPERATURE
          )
        )
      : temperature;
    return this._temperature
      ? `${temperature.toString()}${temperatureUnit}`
      : '';
  }

  public formatValue(value: string): string {
    if (value != null && !isNaN(Number(value))) {
      const temperature = Math.round(
        this.temperatureConverterService.convert(
          Number(value),
          TemperatureConverterService.DEFAULT_TEMPERATURE
        )
      );
      return `${temperature.toString()}`;
    } else {
      return value;
    }
  }

  public getAutoOption(): any {
    return !this.isNestThermostat()
      ? {
          displayName: _.get(
            this.translationService.getResource(),
            'AR.CONDITIONS_NL.AUTO',
            ''
          ),
          name: 'Auto',
          value: 1
        }
      : {
          displayName: _.get(
            this.translationService.getResource(),
            'AR.CONDITIONS_NL.HEAT_COOL',
            ''
          ),
          name: 'Heat-Cool',
          value: 1
        };
  }

  public isNestThermostat() {
    return _.values(nestDeviceTypes)
      .map((type) => type.model)
      .some(
        (model) =>
          model === _.get(this.device, 'config.model', '').toLowerCase()
      );
  }

  public getConditionAttribute(): string {
    return 'SystemMode';
  }

  public getConditionOptions(): any {
    return [
      {
        displayName: _.get(
          this.translationService.getResource(),
          'AR.CONDITIONS_NL.OFF',
          ''
        ),
        name: 'Off',
        value: 0
      },
      this.getAutoOption(),
      {
        displayName: _.get(
          this.translationService.getResource(),
          'AR.CONDITIONS_NL.HEAT',
          ''
        ),
        name: 'Heat',
        value: 4
      },
      {
        displayName: _.get(
          this.translationService.getResource(),
          'AR.CONDITIONS_NL.COOL',
          ''
        ),
        name: 'Cool',
        value: 3
      }
    ];
  }

  public getEffectOptions(): any {
    return [
      {
        displayName: _.get(
          this.translationService.getResource(),
          'AR.CONDITIONS_NL.OFF',
          ''
        ),
        name: 'Off',
        value: 0
      },
      this.getAutoOption(),
      {
        displayName: _.get(
          this.translationService.getResource(),
          'AR.CONDITIONS_NL.HEAT',
          ''
        ),
        name: 'Heat',
        value: 4
      },
      {
        displayName: _.get(
          this.translationService.getResource(),
          'AR.CONDITIONS_NL.COOL',
          ''
        ),
        name: 'Cool',
        value: 3
      }
    ];
  }

  public getEffectAttribute(): string {
    return 'SystemMode';
  }

  public getEffectMethod(context?: any): string | null {
    return null;
  }

  public setValue(value: number, attribute?: string, subscribe?: boolean): any {
    switch (attribute) {
      case 'OccupiedCoolingSetpoint':
        this._desiredCoolingTemperature = value;
        this.setAttributeWithSubscribeOption(
          this.componentId,
          this.processorName,
          'OccupiedCoolingSetpoint',
          { value: this._desiredCoolingTemperature * 100 },
          subscribe || false
        );
        break;
      case 'OccupiedHeatingSetpoint':
        this._desiredHeatingTemperature = value;
        this.setAttributeWithSubscribeOption(
          this.componentId,
          this.processorName,
          'OccupiedHeatingSetpoint',
          { value: this._desiredHeatingTemperature * 100 },
          subscribe || false
        );
        break;
    }
  }

  public getStatus(
    context?: string,
    force?: boolean,
    subscribe?: boolean,
    payload?: any,
    sendDirect: boolean = false
  ): void {
    if (context === 'OccupiedCoolingSetpoint') {
      this.getCache('OccupiedCoolingSetpoint').subscribe((cached: any) => {
        if (cached == null) {
          this.getAttributeWithSubscribeOption(
            this.componentId,
            this.processorName,
            'OccupiedCoolingSetpoint',
            null,
            false,
            `cacheTTL=${CACHE_TIME}`
          );
        } else {
          this._desiredCoolingTemperature = cached;
          this._onUpdate.next({
            OccupiedCoolingSetpoint: this._desiredCoolingTemperature
          });
        }
      });
    } else if (context === 'OccupiedHeatingSetpoint') {
      this.getCache('OccupiedHeatingSetpoint').subscribe((cached: any) => {
        if (cached == null) {
          this.getAttributeWithSubscribeOption(
            this.componentId,
            this.processorName,
            'OccupiedHeatingSetpoint',
            null,
            false,
            `cacheTTL=${CACHE_TIME}`
          );
        } else {
          this._desiredHeatingTemperature = cached;
          this._onUpdate.next({
            OccupiedHeatingSetpoint: this._desiredHeatingTemperature
          });
        }
      });
    } else {
      return super.getStatus(context, force, subscribe, payload, sendDirect);
    }
  }

  public getSelectedValues(): any[] {
    return [this._mode];
  }

  public selectValue(option: any, subscribe?: boolean): any {
    this._mode = option;
    this._onUpdate.next({ mode: this._mode });

    return this.setMode(option, subscribe);
  }

  public getCachedValues(subscribe?: boolean): any {
    const cacheValues = [
      this.getCache('OccupiedCoolingSetpoint'),
      this.getCache('OccupiedHeatingSetpoint'),
      this.getCache('SystemMode'),
      this.getCache()
    ];

    const observable = Observable.forkJoin(cacheValues).flatMap((array) => {
      if (array && array[0] != null) {
        this._desiredCoolingTemperature = array[0];
      }

      if (array && array[1] != null) {
        this._desiredHeatingTemperature = array[1];
      }

      if (array && array[2] != null) {
        this._mode = array[2];
      }

      if (array && array[3] != null) {
        this._temperature = array[3];
      }

      if (this._temperature != null) {
        this.device.cached = true;
      }

      this._onUpdate.next({
        OccupiedCoolingSetpoint: this._desiredCoolingTemperature,
        OccupiedHeatingSetpoint: this._desiredHeatingTemperature,
        SystemMode: this._mode,
        temperature: this._temperature
      });

      return Observable.of(this._temperature);
    });

    if (subscribe === true) {
      return observable;
    }

    const subscription: Subscription = observable.subscribe(() =>
      subscription.unsubscribe()
    );
  }

  public getStatusPayload(): any {
    return [
      'LocalTemperature',
      'SystemMode',
      'OccupiedCoolingSetpoint',
      'OccupiedHeatingSetpoint'
    ];
  }

  public processMessage(
    message: IWebSocketDeviceMessage,
    timestamp?: number
  ): any {
    const attribute = message && message.attributeOrMethod;

    if (attribute === 'LocalTemperature') {
      const response = message && message.data && message.data.response;

      this._temperature = response.value / 100;
      this.state = this._temperature;

      this._onUpdate.next({ temperature: this._temperature });
    } else if (attribute === 'SystemMode') {
      const response = message && message.data && message.data.response;

      this._mode = parseInt(response.value);

      this.setCache('SystemMode', this._mode, CACHE_TIME, undefined, timestamp);

      this._onUpdate.next({ SystemMode: this._mode });
    } else if (attribute === 'OccupiedCoolingSetpoint') {
      const response = message && message.data && message.data.response;

      this._desiredCoolingTemperature = response.value / 100;

      this.setCache(
        'OccupiedCoolingSetpoint',
        this._desiredCoolingTemperature,
        CACHE_TIME,
        undefined,
        timestamp
      );

      this._onUpdate.next({
        OccupiedCoolingSetpoint: this._desiredCoolingTemperature
      });
    } else if (attribute === 'OccupiedHeatingSetpoint') {
      const response = message && message.data && message.data.response;

      this._desiredHeatingTemperature = response.value / 100;

      this.setCache(
        'OccupiedHeatingSetpoint',
        this._desiredHeatingTemperature,
        CACHE_TIME,
        undefined,
        timestamp
      );

      this._onUpdate.next({
        OccupiedHeatingSetpoint: this._desiredHeatingTemperature
      });
    }

    return this.state;
  }

  public getRawValue(
    value: number,
    attribute?: string,
    method?: string
  ): number {
    if (
      !attribute ||
      attribute === 'LocalTemperature' ||
      attribute === 'OccupiedCoolingSetpoint' ||
      attribute === 'OccupiedHeatingSetpoint'
    ) {
      return this.pluginUtilsService.getValueByMultiplying(value, 100);
    } else {
      return value;
    }
  }

  public getProcessedValue(value: any, attribute?: string, method?: string) {
    if (
      !attribute ||
      attribute === 'LocalTemperature' ||
      attribute === 'OccupiedCoolingSetpoint' ||
      attribute === 'OccupiedHeatingSetpoint'
    ) {
      return this.pluginUtilsService.getValueByDividing(value, 100);
    } else {
      return value;
    }
  }
}
